import React, { useState, useEffect, Fragment } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { DFPSlotsProvider, AdSlot } from 'react-dfp';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';

import { AppQueryParams, Languages } from '../../constants';
import { useApplicationParams, useLanguage } from '../../context';
import Header from '../layout/Header';
import FreeCarousel from '../shop/FreeCarousel';
import ShopItem from '../shop/ShopItem';
import ProductItem from '../shop/ProductItem';
import HeaderWeb from '../layout/HeaderWeb';
import Helpers from '../../utils/Helpers';
import CustomerSupportButton from '../shop/CustomerSupportButton';
import { useEventTracker } from '../hooks';

const BANNER_SECTION_TYPES = [1, 8];
const PRODUCT_SECTION_TYPES = [2, 5, 6, 7];
const CELEBRITY_SECTION_TYPE = 3;
const AD_SECTION_TYPE = 4;

const Home = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [sectionsData, setSectionsData] = useState([]);

  const { t } = useTranslation();

  const [searchParams] = useSearchParams();

  const shopId = searchParams.get(AppQueryParams.ShopId) || '';
  const productId = searchParams.get(AppQueryParams.ProductId) || '';

  const navigate = useNavigate();

  const { isWeb, appVersion, platform, deviceId, userToken = '' } = useApplicationParams();
  const { language } = useLanguage();

  const trackEvent = useEventTracker();

  const isMobile = Helpers.getIsMobile();

  useEffect(() => {
    trackEvent('Shop_Screen');
  }, []);

  useEffect(() => {
    const queryUserToken = searchParams.get(AppQueryParams.UserToken);

    if (!queryUserToken) {
      localStorage.removeItem(AppQueryParams.UserToken);
    }
  }, []);

  useEffect(() => {
    const cancelToken = axios.CancelToken.source();

    const getSectionsData = async () => {
      try {
        setIsLoading(true);

        const requestConfig = {
          headers: {
            'Content-Type': 'application/json',
            'x-api-version': appVersion || '4.11.1',
            'x-api-key': process.env.REACT_APP_API_KEY,
            'x-api-os': platform || 'android',
            'x-user-lang': language || Languages.English,
            'x-api-device-id': deviceId || 'CBC64810-FF0C-4008-9558-A999C18085E7',
            authorization: userToken || '',
          },
          cancelToken: cancelToken.token,
        };

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/shopv2/sections${isWeb ? '?source=web' : ''}`,
          requestConfig,
        );

        if (response.data.status !== 'success') {
          return;
        }

        const loadSectionTasks = response.data.data.map((section) => {
          let sectionUrl = `${process.env.REACT_APP_API_URL}/shopv2/section`;

          if (BANNER_SECTION_TYPES.includes(section.itemType)) {
            sectionUrl = `${sectionUrl}/banner?sectionId=${section.id}${
              isWeb ? '&source=web' : ''
            }`;
          } else if (PRODUCT_SECTION_TYPES.includes(section.itemType)) {
            sectionUrl = `${sectionUrl}/products?sectionId=${section.id}&page=0&maximumOffset=20${
              isWeb ? '&source=web' : ''
            }`;
          } else if (section.itemType === CELEBRITY_SECTION_TYPE) {
            sectionUrl = `${sectionUrl}/shops?sectionId=${section.id}${isWeb ? '&source=web' : ''}`;
          } else if (section.itemType === AD_SECTION_TYPE) {
            sectionUrl = `${sectionUrl}/ad?sectionId=${section.id}${isWeb ? '&source=web' : ''}`;
          }

          try {
            return axios.get(sectionUrl, requestConfig).then((response) =>
              response.data.status === 'success'
                ? {
                    id: section.id,
                    title: section.title,
                    itemType: section.itemType,
                    thumbnail: section.thumbnail,
                    data: response.data.data,
                  }
                : null,
            );
          } catch (error) {
            console.error(`Error load section ${section.id}: ${error?.message}`);

            return null;
          }
        });

        const sections = await Promise.all(loadSectionTasks);

        setSectionsData(sections.filter((s) => !!s));
      } finally {
        setIsLoading(false);
      }
    };

    getSectionsData();

    return () => {
      cancelToken.cancel();
    };
  }, [language]);

  const handleViewSection = (section) => {
    trackEvent('product_section_clicked', {
      source: 'shop',
      section_id: section.id,
      section_name: section.title,
      is_guest: !userToken,
    });

    navigate(
      `/shop/sections/${section.id}?title=${section.title}&type=${section.itemType}&from=home`,
    );
  };

  if (shopId) {
    <Navigate to={`/shop/sections/${shopId}?from=home&deeplink=true`} />;
  }

  if (productId) {
    <Navigate to={`/shop/products/${productId}?from=home&deeplink=true`} />;
  }

  return (
    <>
      {isWeb ? <HeaderWeb /> : <Header loadingPage={isLoading} />}
      <article className={`home-article ${isWeb && 'container container-lg home-article-lg'}`}>
        {isWeb && <CustomerSupportButton />}
        {isLoading ? (
          <div className="loader mt-5" />
        ) : (
          sectionsData.map((sectionItem) => (
            <Fragment key={sectionItem.id}>
              {(sectionItem.itemType === 1 || sectionItem.itemType === 8) && (
                <section className="banner-section pad">
                  <img src={sectionItem.data.bannerContentURL} alt='' className='banner-img' />
                </section>
              )}
              {sectionItem.itemType === 3 && (
                <section className={`shop-section ${isWeb && 'shop-section-lg'}`}>
                  <div className="section-header-div">
                    <p className="section-title">{sectionItem.title}</p>
                    <p className="vis-hidden">{t('view_all')}</p>
                  </div>
                  <div>
                    <FreeCarousel
                      ItemComponent={ShopItem}
                      data={sectionItem.data ? sectionItem.data : []}
                      sectionId={uuidv4()}
                      minCount={7}
                      childCount={sectionItem.data.length}
                    />
                  </div>
                </section>
              )}
              {PRODUCT_SECTION_TYPES.includes(sectionItem.itemType) && (
                <section className={`home-products-section ${isWeb && 'home-products-section-lg'}`}>
                  <div className="section-header-div">
                    <div className="celebrity-section">
                      {sectionItem.thumbnail && (
                        <div className="section-thumbnail-container">
                          <img
                            alt={sectionItem.title || 'category type'}
                            src={sectionItem.thumbnail}
                            className="section-thumbnail"
                          />
                        </div>
                      )}
                      <p className="section-title">{sectionItem.title}</p>
                    </div>
                    <span
                      onClick={() => handleViewSection(sectionItem)}
                      className={`section-view-all ${sectionItem.thumbnail && 'with-image'}`}
                    >
                      {t('view_all')}
                    </span>
                  </div>
                  <div>
                    <FreeCarousel
                      ItemComponent={ProductItem}
                      data={sectionItem.data ? sectionItem.data : []}
                      showHalfOfNext
                      sectionId={uuidv4()}
                      minCount={isMobile ? 2 : 4}
                      childCount={sectionItem.data.length}
                    />
                  </div>
                </section>
              )}
              {sectionItem.itemType === 4 && (
                <section className="banner-section pad">
                  {sectionItem.data.source === 'adManager' && (
                    <div className="admanager-ad">
                      <DFPSlotsProvider dfpNetworkId="">
                        <AdSlot
                          adUnit={sectionItem.data.data.adUnitId}
                          // adUnit='/27367402/360VUZ_ANDROID_TEST'
                          sizes={[[sectionItem.data.data.width, sectionItem.data.data.height]]}
                        />
                      </DFPSlotsProvider>
                    </div>
                  )}
                </section>
              )}
            </Fragment>
          ))
        )}
      </article>
    </>
  );
};

export default Home;
